import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DonationForm } from '@feathr/blackbox';
import { ConfirmModal, Input, toast } from '@feathr/components';
import { errorMessage } from '@feathr/hooks';

import { validateDonationFormName } from './DonationFormRenameModal.utils';

export interface IDonationFormRenameModalProps {
  form: DonationForm;
  opened: boolean;
  onClose: () => void;
}

function DonationFormRenameModal({
  form,
  opened,
  onClose,
}: Readonly<IDonationFormRenameModalProps>): JSX.Element {
  const { t } = useTranslation();
  const [newName, setNewName] = useState<string | undefined>(form.name);

  useEffect(() => {
    if (opened) {
      setNewName(form.name);
    }
  }, [opened, form.name]);

  const validationErrors = validateDonationFormName({ oldName: form.name, newName });

  async function handleRename(): Promise<void> {
    if (validationErrors.length > 0 || newName === undefined) {
      return;
    }
    const oldName = form.name;
    try {
      form.set({ name: newName });
      await form.patchDirty();

      if (form.isErrored && form.error) {
        throw form.error;
      }

      toast(
        t('Renamed "{{- oldName}}" to "{{- newName}}".', {
          oldName,
          newName,
        }),
        { type: 'success' },
      );
      onClose();
    } catch (error) {
      toast(
        t("Couldn't rename donation form. {{- message}}", {
          message: errorMessage(error, t),
        }),
        { type: 'error' },
      );
    }
  }

  return (
    <ConfirmModal
      confirmButtonText={t('Rename')}
      confirmDisabled={validationErrors.length > 0}
      onClose={onClose}
      onConfirm={handleRename}
      opened={opened}
      t={t}
      title={t('Rename donation form')}
    >
      <Input
        label={t('Form name')}
        onChange={setNewName}
        validationError={opened ? validationErrors : undefined}
        value={newName}
      />
    </ConfirmModal>
  );
}

export default observer(DonationFormRenameModal);
